import React, { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "../../../../components/ui/sheet";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "../../../../components/ui/avatar";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import {
  RadioGroup,
  RadioGroupItem,
} from "../../../../components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../components/ui/select";
import { Spinner } from "../../../../components/ui/spinner";
import { Switch } from "../../../../components/ui/switch";
import { UpsellRocketIcon } from "../../../../components/ui/icon";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../../../../components/ui/tooltip";
import { cn } from "../../../../lib/utils";
import { SkedCheckbox, TextInput } from "../../../../ui";

import {
  AccountMultiSelectMenu,
  accountTypesToSelectorAccounts,
  accountTypesToSelectorGroups,
} from "../../../../components/ui/selectors";
import { CollectionSettings } from "../edit-user/collectionSettings";
import AllowPostStatusList from "../edit-user/AllowPostStatusList";
import { useMediaCollections } from "../../../../entities/media-collections";
import type { MediaCollection } from "../../../../api/media-library";

import { filterAccountsByText } from "../../../../utils/accounts";
import {
  hasApprovals,
  hasExternalApprovals,
} from "../../../../utils/feature-gates";
import { useExternalIntegrationsAccess } from "../../../../pages/profile-settings/integrations-hub/access";
import { entitlementsPermission } from "../../../../utils/entitlements-permission";
import { goToManagePlan } from "../../../../utils/manage-plan";
import { getUserFullName, getUserNameInitials } from "../../../../utils/user";
import { useTrackEvent } from "../../../../libs/analytics";
import { ADD_USER_CLICKED } from "../../../../constants/Events";
import { INBOX_V2_EVENTS } from "../../../../@inbox-v2/analytics";

import { getUser as getCurrentUser } from "../../../../selectors/commonSelectors";
import {
  getAccounts,
  getSelectedAccountIds,
} from "../../../../selectors/skedCoreSelectors";

import {
  collaborationUserKeys,
  fetchCollaborationUserByEmail,
  useCollaborationSeatUsage,
  useCollaborationUser,
  useInviteCollaborationUser,
  useUpdateCollaborationUserAccess,
  useUpdateCollaborationUserPreferences,
} from "../../users-queries";
import { openModal } from "../../../../libs/modals";
import { DELETE_USER_MODAL, INVITATION_USER_MODAL } from "../../UserModals";
import type { CollaborationPostStatus } from "../../../user";
import type { AppDispatch } from "../../../../types/redux-dispatch";

import { statusListAllows } from "../../../../components/Collaboration/StatusChange/status-change-permissions";
import {
  getEffectivePostStatuses,
  isPostStatusMigrationPending,
} from "../../../../utils/post-statuses";

import { PermissionSection, SECTION_CARD_CLASS } from "./permission-section";
import { useInstagramApiPublishingOn } from "../../../../utils/feature-toggle-utils";

export type UserSheetMode = "create" | "edit";

type Status = CollaborationPostStatus & { selected: boolean };
type ReportBuilderPermission = "full-access" | "no-access" | "download-only";
type InboxPermissionLevel = "read-only" | "personal" | "full-access";
type SocialListeningPermissions = {
  hasSentiment: boolean;
  hasAudience: boolean;
  hasCompetitorView: boolean;
  hasCompetitorEdit: boolean;
  hasKeywords: boolean;
};
type SheetUserDetail = {
  _id?: string;
  userId?: string;
  email?: string;
  fullName?: string;
  firstName?: string;
  lastName?: string;
  canAddAccounts?: boolean;
  canManageUsers?: boolean;
  canConfigureIntegrations?: boolean;
  showInbox?: boolean;
  inboxBoardsAndFeedsPermission?: InboxPermissionLevel;
  inboxReplyTemplatesPermission?: InboxPermissionLevel;
  hideOtherCollections?: boolean;
  hideReviewLink?: boolean;
  canCreateExternalComments?: boolean;
  canManageContentCollects?: boolean;
  fbAdAccountPermission?: { fullAccess?: boolean };
  reportBuilderPermission?: ReportBuilderPermission;
  socialListeningPermission?: Partial<SocialListeningPermissions>;
  preferences?: { canBoostPosts?: boolean };
  canBoostPosts?: boolean;
  canChangeStatusTo?: string[];
  collectionUserCanAccess?: string[] | string | null;
  accessibleAccounts?: string[];
};

const EMPTY_SHEET_USER_DETAIL: SheetUserDetail = {};

interface UserSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: UserSheetMode;
  /**
   * Existing user info for edit mode. Create mode doesn't need it — identity
   * comes from the form fields.
   */
  userInfo?: {
    _id?: string;
    email: string;
    firstName?: string;
    lastName?: string;
    fullName?: string;
    profileImage?: string;
    status?: string;
  };
}

const sectionStackClass = "flex flex-col gap-0";

function Checkbox({
  className,
  ...props
}: React.ComponentProps<typeof SkedCheckbox>) {
  return (
    <SkedCheckbox {...props} className={cn("block leading-[1.4]", className)} />
  );
}

const INBOX_PERMISSION_LABELS: Record<InboxPermissionLevel, string> = {
  "read-only": "Read only",
  personal: "Personal",
  "full-access": "Full access",
};

const BOARD_FEED_PERMISSION_DESCRIPTIONS: Record<InboxPermissionLevel, string> =
  {
    "read-only": "User cannot create boards and feeds",
    personal: "User can only create, edit and delete personal boards and feeds",
    "full-access":
      "User can create, edit, and delete organizational and personal boards & feeds",
  };

const REPLY_TEMPLATE_PERMISSION_DESCRIPTIONS: Record<
  InboxPermissionLevel,
  string
> = {
  "read-only": "User cannot create reply templates",
  personal: "User can only create, edit, and delete personal reply templates",
  "full-access":
    "User can create, edit, and delete organizational and personal reply templates",
};

function InboxPermissionSelect({
  descriptions,
  label,
  onValueChange,
  value,
}: {
  descriptions: Record<InboxPermissionLevel, string>;
  label: string;
  onValueChange: (value: InboxPermissionLevel) => void;
  value: InboxPermissionLevel;
}) {
  const id = `inbox-permission-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`;

  return (
    <label
      htmlFor={id}
      className="flex flex-col gap-1 text-sm font-medium text-neutral-900"
    >
      {label}
      <Select
        value={value}
        onValueChange={(next) => onValueChange(next as InboxPermissionLevel)}
      >
        <SelectTrigger id={id} className="h-8 rounded-lg px-3 py-1.5 shadow-xs">
          <SelectValue>{INBOX_PERMISSION_LABELS[value]}</SelectValue>
        </SelectTrigger>
        <SelectContent className="rounded-lg border-neutral-200 shadow-md">
          {(Object.keys(INBOX_PERMISSION_LABELS) as InboxPermissionLevel[]).map(
            (permission) => (
              <SelectItem
                key={permission}
                value={permission}
                className="items-start rounded-none px-3 py-1.5 pr-9 hover:bg-blue-25 focus:bg-blue-25"
              >
                <span className="flex flex-col gap-0.5 text-left">
                  <span className="font-medium leading-5 text-neutral-900">
                    {INBOX_PERMISSION_LABELS[permission]}
                  </span>
                  <span className="whitespace-normal font-normal leading-5 text-neutral-600">
                    {descriptions[permission]}
                  </span>
                </span>
              </SelectItem>
            )
          )}
        </SelectContent>
      </Select>
    </label>
  );
}

export function UserSheet({
  open,
  onOpenChange,
  mode,
  userInfo,
}: UserSheetProps): JSX.Element {
  const dispatch = useDispatch<AppDispatch>();
  const queryClient = useQueryClient();
  const trackEvent = useTrackEvent();
  const [submitting, setSubmitting] = useState(false);
  const currentUser = useSelector(getCurrentUser);
  const integrationsEnabled = useExternalIntegrationsAccess(currentUser);
  const accounts = useSelector(getAccounts);
  const selectedAccounts = useSelector(getSelectedAccountIds);
  const adminUser = currentUser;

  const { collections, isLoading: isLoadingMedia } = useMediaCollections();

  const isNewUser = mode === "create";
  const userQuery = useCollaborationUser(
    !isNewUser && open ? userInfo?.email : undefined
  );
  const user = (
    isNewUser
      ? EMPTY_SHEET_USER_DETAIL
      : userQuery.data || EMPTY_SHEET_USER_DETAIL
  ) as SheetUserDetail;
  const requestedEmail = userInfo?.email?.toLowerCase();
  const fetchedUserMatches =
    isNewUser ||
    (!!userQuery.data &&
      ((userInfo?._id &&
        (userQuery.data.userId === userInfo._id ||
          userQuery.data._id === userInfo._id)) ||
        (!!requestedEmail &&
          (!userQuery.data.email ||
            userQuery.data.email.toLowerCase() === requestedEmail))));
  const isLoaded = isNewUser || (userQuery.isSuccess && fetchedUserMatches);
  const userLoadError = !isNewUser && userQuery.isError;
  const inviteUserMutation = useInviteCollaborationUser();
  const { data: seatUsage } = useCollaborationSeatUsage({
    enabled: isNewUser,
  });
  // SKE-5085: users plus pending invites fill every seat. The server fails
  // closed when it can't count, which arrives with no known limit.
  const seatsFull = isNewUser && !!seatUsage?.inviteBlocked;
  const seatCheckFailed = seatsFull && seatUsage?.maximumUsers === null;
  const updateAccessMutation = useUpdateCollaborationUserAccess();
  const updatePreferencesMutation = useUpdateCollaborationUserPreferences();
  const isLoading =
    userQuery.isLoading ||
    inviteUserMutation.isPending ||
    updateAccessMutation.isPending ||
    updatePreferencesMutation.isPending ||
    submitting;

  const tier = entitlementsPermission.getUserPermissionsTier(currentUser);
  const isBasicTier = tier === "basic";
  // Entitlement-driven (user-permissions=advanced): per-collection access
  // restriction, previously Enterprise-only, now available to Accelerate+.
  const canRestrictCollections = tier === "advanced";
  // SKE-4121: status restrictions follow approvals, not the permissions tier.
  const canEditPostStatusRestrictions = hasApprovals(currentUser);
  const effectivePostStatuses = useMemo(
    () => getEffectivePostStatuses(currentUser),
    [currentUser]
  );
  const isStatusProjectionPending = isPostStatusMigrationPending(
    effectivePostStatuses
  );

  const [email, setEmail] = useState(userInfo?.email ?? "");
  const [firstName, setFirstName] = useState(userInfo?.firstName ?? "");
  const [lastName, setLastName] = useState(userInfo?.lastName ?? "");
  const [createErrors, setCreateErrors] = useState<string[]>([]);
  // SKE-3533: surfaces when the server's invite endpoint blocks because the
  // workspace has hit its `maximum-users` cap (server message starts with
  // "Your team is full"). Drives a friendlier inline banner with a direct
  // link to /onboarding/manage-plan instead of just dumping the raw error.
  const [userLimitReached, setUserLimitReached] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [accessibleAccounts, setAccessibleAccounts] = useState<string[]>([]);
  const [postStatusList, setPostStatusList] = useState<Status[]>([]);
  const [canAddAccounts, setCanAddAccounts] = useState(false);
  const [canManageUsers, setCanManageUsers] = useState(false);
  const [canConfigureIntegrations, setCanConfigureIntegrations] =
    useState(false);
  const [canShowInbox, setShowInbox] = useState(true);
  const [inboxBoardsAndFeedsPermission, setInboxBoardsAndFeedsPermission] =
    useState<InboxPermissionLevel>("personal");
  const [inboxReplyTemplatesPermission, setInboxReplyTemplatesPermission] =
    useState<InboxPermissionLevel>("personal");
  const [canAccessPaidSocial, setCanAccessPaidSocial] = useState(false);
  const [canBoostPosts, setCanBoostPosts] = useState(false);
  const [reportBuilderPermission, setReportBuilderPermission] =
    useState<ReportBuilderPermission>("full-access");
  const [socialListeningPermission, setSlPermission] =
    useState<SocialListeningPermissions>({
      hasSentiment: false,
      hasAudience: false,
      hasCompetitorView: false,
      hasCompetitorEdit: false,
      hasKeywords: false,
    });
  const [collectionUserCanAccess, setCollectionUserCanAccess] = useState<
    MediaCollection | string[] | null
  >(canRestrictCollections ? [] : null);
  const [hideOtherCollections, setHideOtherCollections] = useState(false);
  const [hideReviewLink, setHideReviewLink] = useState(false);
  const [canCreateExternalComments, setCanCreateExternalComments] =
    useState(true);
  const [canManageContentCollects, setCanManageContentCollects] =
    useState(true);
  const [accountFilterText, setAccountsFilterText] = useState("");

  const slEnabled = entitlementsPermission.hasSocialListening(currentUser);
  const canAccessExternalReviewLinks = hasExternalApprovals(currentUser);
  const isShowInboxSection = adminUser?.preferences?.showInbox;

  const slSettingsConfig = useMemo(
    () => [
      {
        name: "Sentiment",
        settingsParam: "hasSentiment" as const,
        enabled:
          entitlementsPermission.hasSocialListeningSentiment(currentUser),
      },
      {
        name: "Audience",
        settingsParam: "hasAudience" as const,
        enabled: entitlementsPermission.hasSocialListeningAudience(currentUser),
      },
      {
        name: "Competitors: View",
        settingsParam: "hasCompetitorView" as const,
        enabled:
          entitlementsPermission.hasSocialListeningCompetitors(currentUser),
      },
      {
        name: "Competitors: Edit",
        settingsParam: "hasCompetitorEdit" as const,
        enabled:
          entitlementsPermission.hasSocialListeningCompetitors(currentUser),
      },
      {
        name: "Keywords",
        settingsParam: "hasKeywords" as const,
        enabled: entitlementsPermission.hasSocialListeningKeyword(currentUser),
      },
    ],
    [currentUser]
  );

  // Reset both the redux per-user slice and the local create-form fields when
  // the sheet opens in create mode so a previously-typed-then-cancelled invite
  // doesn't leak into the next open.
  useEffect(() => {
    if (open && isNewUser) {
      setEmail(userInfo?.email ?? "");
      setFirstName(userInfo?.firstName ?? "");
      setLastName(userInfo?.lastName ?? "");
      setCreateErrors([]);
      setUserLimitReached(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    isNewUser,
    userInfo?.email,
    userInfo?.firstName,
    userInfo?.lastName,
  ]);

  // Prefill permissions from the fetched user, or defaults for a new invite.
  useEffect(() => {
    if (!open) return;
    if (!(isLoaded || isNewUser)) return;

    if (isNewUser) {
      setCanAddAccounts(false);
      setCanManageUsers(false);
      setShowInbox(true);
      setInboxBoardsAndFeedsPermission("personal");
      setInboxReplyTemplatesPermission("personal");
      setHideOtherCollections(false);
      setHideReviewLink(false);
      setCanCreateExternalComments(true);
      setCanManageContentCollects(true);
      setCanAccessPaidSocial(true);
      setCanBoostPosts(false);
      setReportBuilderPermission("full-access");
      setSlPermission({
        hasSentiment: true,
        hasAudience: true,
        hasCompetitorView: true,
        hasCompetitorEdit: true,
        hasKeywords: true,
      });
    } else {
      setCanAddAccounts(!!user.canAddAccounts);
      setCanManageUsers(Boolean(user.canManageUsers));
      setCanConfigureIntegrations(Boolean(user.canConfigureIntegrations));
      setShowInbox(Boolean(user.showInbox));
      setInboxBoardsAndFeedsPermission(
        user.inboxBoardsAndFeedsPermission ?? "personal"
      );
      setInboxReplyTemplatesPermission(
        user.inboxReplyTemplatesPermission ?? "personal"
      );
      setHideOtherCollections(Boolean(user.hideOtherCollections));
      setHideReviewLink(!!user.hideReviewLink);
      setCanCreateExternalComments(user.canCreateExternalComments !== false);
      setCanManageContentCollects(user.canManageContentCollects !== false);
      setCanAccessPaidSocial(!!user.fbAdAccountPermission?.fullAccess);
      setCanBoostPosts(
        user.preferences?.canBoostPosts === true || user.canBoostPosts === true
      );
      setReportBuilderPermission(
        user.reportBuilderPermission || "download-only"
      );
      setSlPermission({
        hasSentiment: user.socialListeningPermission?.hasSentiment ?? true,
        hasAudience: user.socialListeningPermission?.hasAudience ?? true,
        hasCompetitorView:
          user.socialListeningPermission?.hasCompetitorView ?? true,
        hasCompetitorEdit:
          user.socialListeningPermission?.hasCompetitorEdit ?? true,
        hasKeywords: user.socialListeningPermission?.hasKeywords ?? true,
      });
    }

    if (canEditPostStatusRestrictions) {
      const postStatuses = effectivePostStatuses;
      // Same predicate as the runtime, so an empty list ticks every status.
      const updated = isNewUser
        ? postStatuses.map((s) => ({ ...s, selected: true }))
        : postStatuses.map((s) => ({
            ...s,
            selected:
              statusListAllows(user.canChangeStatusTo, s.key) ||
              (!isStatusProjectionPending && s.isDefault),
          }));
      setPostStatusList(updated);
      if (isNewUser) setCanAccessPaidSocial(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user, isLoaded, isNewUser, currentUser]);

  // Collection selection mirrors per-user data / defaults.
  useEffect(() => {
    if (!open) return;
    if (!(isLoaded && collections && collections.length > 0)) return;
    if (canRestrictCollections) {
      if (!user.collectionUserCanAccess) {
        setCollectionUserCanAccess([]);
      } else {
        setCollectionUserCanAccess(
          Array.isArray(user.collectionUserCanAccess)
            ? user.collectionUserCanAccess
            : [user.collectionUserCanAccess]
        );
      }
    } else {
      const collection = user.collectionUserCanAccess
        ? (collections?.find(
            (c: any) => c._id === user.collectionUserCanAccess
          ) ?? null)
        : collections[0];
      setCollectionUserCanAccess(collection);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isLoaded, collections, user.collectionUserCanAccess]);

  // Accessible accounts: existing user's allow-list, or all currently selected
  // accounts as a default on invite.
  useEffect(() => {
    if (!open) return;
    if (!(isLoaded || isNewUser)) return;
    if (isNewUser) {
      setAccessibleAccounts(selectedAccounts ?? []);
    } else if (user?.accessibleAccounts?.length) {
      const uniq: string[] = [];
      user.accessibleAccounts.forEach((id: string) => {
        if (!uniq.includes(id)) uniq.push(id);
      });
      setAccessibleAccounts(uniq);
    } else {
      setAccessibleAccounts([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user, isLoaded, isNewUser, selectedAccounts]);

  const fullName = useMemo(() => {
    if (isNewUser) {
      return getUserFullName({ firstName, lastName, fullName: undefined });
    }
    return getUserFullName({
      fullName: userInfo?.fullName || user.fullName,
      lastName: userInfo?.lastName || user.lastName,
      firstName: userInfo?.firstName || user.firstName,
    });
  }, [isNewUser, firstName, lastName, userInfo, user]);

  const avatarInitials = useMemo(
    () =>
      getUserNameInitials({
        firstName: isNewUser
          ? firstName
          : userInfo?.firstName || user.firstName,
        lastName: isNewUser ? lastName : userInfo?.lastName || user.lastName,
      }),
    [isNewUser, firstName, lastName, userInfo, user]
  );

  const filteredAccounts = useMemo(
    () => accounts.filter(filterAccountsByText(accountFilterText)),
    [accounts, accountFilterText]
  );
  const instagramApiPublishing = useInstagramApiPublishingOn();
  const accountMenuAccounts = useMemo(
    () =>
      accountTypesToSelectorAccounts(filteredAccounts, {
        instagramApiPublishing,
      }),
    [filteredAccounts, instagramApiPublishing]
  );
  const accountMenuGroups = useMemo(
    () =>
      accountTypesToSelectorGroups(filteredAccounts, {
        instagramApiPublishing,
      }),
    [filteredAccounts, instagramApiPublishing]
  );

  const disabledSubmit =
    !(isLoaded || isNewUser) ||
    seatsFull ||
    (isNewUser && !(email && firstName && lastName));

  function handleClose() {
    if (isLoading) return;
    setSubmitError(null);
    onOpenChange(false);
  }

  function handleDelete() {
    if (!userInfo?.email) return;
    dispatch(
      openModal({
        type: DELETE_USER_MODAL,
        props: { email: userInfo.email, accountName: fullName },
      })
    );
  }

  async function submit() {
    const collectionEmpty = Array.isArray(collectionUserCanAccess)
      ? collectionUserCanAccess.length === 0
      : false;

    if (hideOtherCollections && (!collectionUserCanAccess || collectionEmpty)) {
      toast.error(
        "Please select at least one collection the user should be restricted to."
      );
      return;
    }

    setSubmitting(true);
    setCreateErrors([]);
    setUserLimitReached(false);
    setSubmitError(null);

    type SheetUser = {
      userId?: string;
      email?: string;
      [key: string]: unknown;
    };

    let userForUpdate: SheetUser = { ...(user as SheetUser) };
    let targetEmail = userInfo?.email ?? "";

    if (isNewUser) {
      trackEvent({ eventName: ADD_USER_CLICKED });
      const payload = { email, firstName, lastName };
      try {
        await inviteUserMutation.mutateAsync(payload);
        userForUpdate = await queryClient.fetchQuery({
          queryKey: collaborationUserKeys.detail(email),
          queryFn: () => fetchCollaborationUserByEmail(email),
        });
        targetEmail = email;
      } catch (error: unknown) {
        const message =
          error instanceof Error
            ? error.message
            : "Failed to invite user. Please try again.";
        if (/User limit reached|Your team is full/i.test(message)) {
          setUserLimitReached(true);
          setCreateErrors([]);
        } else {
          setUserLimitReached(false);
          setCreateErrors([message]);
        }
        setSubmitting(false);
        return;
      }
    } else if (!fetchedUserMatches) {
      setSubmitError(
        "User data is still loading. Please try again in a moment."
      );
      setSubmitting(false);
      return;
    }

    if (!targetEmail) {
      setSubmitError("Save failed - missing user email.");
      setSubmitting(false);
      return;
    }

    const previousPreferences = userForUpdate as SheetUserDetail;
    const selectedStatusKeys = isStatusProjectionPending
      ? previousPreferences.canChangeStatusTo
      : postStatusList.filter((item) => item.selected).map((item) => item.key);

    // Basic tier sends canChangeStatusTo alone, so stale hydration defaults
    // can't persist elevated values for the locked toggles.
    //
    // Defense in depth: this client gate only OMITS advanced permission keys
    // from the PATCH payload; it does NOT prevent a determined caller (e.g.
    // curl) from setting them. The server endpoint
    // `PUT /multiuser/setPreferences/:userId` does not currently validate the
    // actor's tier before accepting advanced fields like
    // `canCreateExternalComments`, `reportBuilderPermission`,
    // `socialListeningPermission`, `canManageUsers`, etc. A basic-tier admin
    // hitting the API directly could persist elevated values until backend
    // validation is added. Backend validation tracked in SKE-3526.
    const preferencesPayload: Record<string, unknown> | null =
      tier === "advanced"
        ? {
            hideReviewLink,
            canAddAccounts,
            reportBuilderPermission,
            socialListeningPermission,
            canChangeStatusTo: selectedStatusKeys,
            canManageUsers,
            canConfigureIntegrations,
            showInbox: canShowInbox,
            inboxBoardsAndFeedsPermission,
            inboxReplyTemplatesPermission,
            hideOtherCollections,
            canCreateExternalComments,
            canManageContentCollects,
            collectionUserCanAccess: canRestrictCollections
              ? collectionUserCanAccess
              : (collectionUserCanAccess as MediaCollection)?._id,
            fbAdAccountPermission: { fullAccess: canAccessPaidSocial },
            canBoostPosts,
          }
        : canEditPostStatusRestrictions && postStatusList.length > 0
          ? {
              canChangeStatusTo: selectedStatusKeys,
              // Echo the stored collection restriction back. A server without
              // the SKE-4121 fallback nulls collectionUserCanAccess for any
              // field the body omits. Drop this once that fallback is live.
              ...(previousPreferences.hideOtherCollections
                ? {
                    hideOtherCollections: true,
                    collectionUserCanAccess:
                      previousPreferences.collectionUserCanAccess,
                  }
                : {}),
            }
          : null;

    if (preferencesPayload && !userForUpdate.userId) {
      setSubmitError("Save failed - missing user ID.");
      setSubmitting(false);
      return;
    }

    // updateUser (accessible accounts) and updateUserPreferences hit different
    // endpoints and don't depend on each other, so fire both concurrently.
    const accessUpdate = updateAccessMutation.mutateAsync({
      email: targetEmail,
      ids: accessibleAccounts,
    });

    const inboxCollabPermissionChanged =
      tier === "advanced" &&
      (Boolean(previousPreferences.showInbox) !== canShowInbox ||
        (previousPreferences.inboxBoardsAndFeedsPermission ?? "personal") !==
          inboxBoardsAndFeedsPermission ||
        (previousPreferences.inboxReplyTemplatesPermission ?? "personal") !==
          inboxReplyTemplatesPermission);

    const prefsUpdate =
      userForUpdate.userId && preferencesPayload
        ? updatePreferencesMutation.mutateAsync({
            userId: userForUpdate.userId,
            email: targetEmail,
            preferences: preferencesPayload,
          })
        : Promise.resolve();

    try {
      await Promise.all([accessUpdate, prefsUpdate]);
    } catch (error: unknown) {
      setSubmitError(
        error instanceof Error
          ? error.message
          : "Save failed - please try again."
      );
      setSubmitting(false);
      return;
    }

    if (inboxCollabPermissionChanged) {
      trackEvent({
        eventName: INBOX_V2_EVENTS.settings,
        metadata: {
          action: "collab-permission-changed",
          setting_type: "collab-access",
        },
      });
    }

    setSubmitting(false);
    onOpenChange(false);

    if (isNewUser) {
      dispatch(
        openModal({
          type: INVITATION_USER_MODAL,
          props: { userName: fullName || email },
        })
      );
    }
  }

  const headerTitle = isNewUser ? "Invite user" : "Edit user access";
  const headerSubtitle = isNewUser
    ? "They'll receive an invite link once you save."
    : fullName || userInfo?.email;

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (isLoading) return;
        if (!next) setSubmitError(null);
        onOpenChange(next);
      }}
    >
      <SheetContent
        showCloseButton={false}
        className="w-full sm:max-w-[560px] overflow-hidden rounded-l-lg border-neutral-100 p-0 font-inter"
      >
        <div className="flex h-full flex-col">
          <SheetHeader className="flex flex-row items-start gap-3 border-b border-neutral-100 px-6 py-4 space-y-0">
            <Avatar className="h-10 w-10 rounded-md">
              {userInfo?.profileImage && (
                <AvatarImage
                  src={userInfo.profileImage}
                  alt={fullName || "User avatar"}
                  className="object-cover"
                />
              )}
              <AvatarFallback className="rounded-md bg-blue-900 text-sm uppercase text-white">
                {avatarInitials || "U"}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <SheetTitle className="font-inter text-lg font-semibold text-neutral-900">
                {headerTitle}
              </SheetTitle>
              <SheetDescription className="text-sm text-neutral-500 truncate">
                {headerSubtitle}
              </SheetDescription>
            </div>
            <button
              type="button"
              onClick={handleClose}
              disabled={isLoading}
              className="flex h-8 w-8 items-center justify-center rounded-md text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700 disabled:pointer-events-none disabled:opacity-50"
              aria-label="Close"
            >
              <i className="fa-solid fa-xmark" aria-hidden />
            </button>
          </SheetHeader>

          {/* Save/submit uses the footer "Saving…" state, so we only gate
              the sheet body on the initial edit-mode fetch. */}
          {userLoadError ? (
            <div className="flex flex-1 items-center justify-center px-6 py-16 text-center text-sm text-red-600">
              There was an error getting data for this user.
            </div>
          ) : (!isNewUser && !isLoaded) || isLoadingMedia ? (
            <div className="flex flex-1 items-center justify-center py-16">
              <Spinner size="lg" />
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto px-6 py-5">
              {isBasicTier && (
                <div className="mb-4 rounded-xl border border-blue-100 bg-blue-50 p-4">
                  <div className="mb-1 flex items-center gap-2">
                    <UpsellRocketIcon bgColor="#eef2ff" color="#4f31ff" />
                    <h3 className="m-0 font-inter text-sm font-semibold text-blue-900">
                      Basic user permissions
                    </h3>
                  </div>
                  <p className="m-0 mb-3 text-sm leading-snug text-blue-900/80">
                    You can choose which social accounts each teammate can see
                    and publish to. Detailed permissions like inbox access and
                    custom reports are available on Accelerate, Enterprise or
                    Custom plans. Open Manage Plan to change to a plan that
                    includes them.
                  </p>
                  <Button
                    variant="blue-primary"
                    size="sm"
                    onClick={() => goToManagePlan()}
                  >
                    Open Manage Plan
                  </Button>
                </div>
              )}

              <div className={sectionStackClass}>
                {isNewUser && (
                  <div className="flex flex-col gap-3">
                    <label className="flex flex-col gap-1 text-xs font-semibold text-neutral-700">
                      Email
                      <Input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="quirky@skedsocial.com"
                        autoFocus
                      />
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <label className="flex flex-col gap-1 text-xs font-semibold text-neutral-700">
                        First name
                        <Input
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                          placeholder="Jane"
                        />
                      </label>
                      <label className="flex flex-col gap-1 text-xs font-semibold text-neutral-700">
                        Last name
                        <Input
                          value={lastName}
                          onChange={(e) => setLastName(e.target.value)}
                          placeholder="Austen"
                        />
                      </label>
                    </div>
                    {createErrors.length > 0 && (
                      <ul className="m-0 list-disc pl-5 text-xs text-red-600">
                        {createErrors.map((err) => (
                          <li key={err}>{err}</li>
                        ))}
                      </ul>
                    )}
                    {(userLimitReached || (seatsFull && !seatCheckFailed)) && (
                      <div
                        role="alert"
                        className="mt-2 flex flex-col gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900"
                      >
                        <div className="font-semibold">Your team is full.</div>
                        <div className="text-amber-800">
                          Every seat on your plan is in use. Pending invites
                          hold a seat until they’re accepted or cancelled.
                          Upgrade your plan or cancel a pending invite to invite
                          someone new.
                        </div>
                        <a
                          href="/onboarding/manage-plan"
                          className="self-start rounded-md bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white no-underline hover:bg-amber-700"
                        >
                          Manage plan
                        </a>
                      </div>
                    )}
                  </div>
                )}

                <section className={SECTION_CARD_CLASS}>
                  <h3 className="font-inter text-sm font-semibold text-neutral-900">
                    Account access
                  </h3>
                  <p className="mb-3 text-sm text-neutral-600">
                    They can post, view and edit drafts, and see archived posts
                    for the accounts selected below.
                  </p>
                  <TextInput
                    className="mb-3 w-full text-sm"
                    onChange={(v: string) => setAccountsFilterText(v.trim())}
                    placeholder="Search accounts"
                  />
                  <div className="max-h-[280px] overflow-y-auto custom-scroller">
                    <AccountMultiSelectMenu
                      accounts={accountMenuAccounts}
                      groups={accountMenuGroups}
                      selectedIds={accessibleAccounts}
                      onChange={setAccessibleAccounts}
                      searchable={false}
                      className="max-h-none border-0 shadow-none"
                      listClassName="max-h-none"
                    />
                  </div>
                </section>

                {hasApprovals(currentUser) &&
                  (() => {
                    // Pick the chip + copy that matches the shortest fix from
                    // the customer's current state. The advanced permissions
                    // tier marks Accelerate, Enterprise and Custom.
                    const onAcceleratePlus = tier === "advanced";
                    const chipText = onAcceleratePlus
                      ? "Add-on"
                      : "Accelerate, Enterprise or Custom";
                    return (
                      <section className={SECTION_CARD_CLASS}>
                        <h3 className="font-inter text-sm font-semibold text-neutral-900">
                          External review access
                        </h3>
                        <p className="mb-3 text-sm text-neutral-600">
                          How teammates work with external review links and
                          reviewer notes.
                        </p>
                        <ul role="list" className="divide-y divide-neutral-100">
                          <li className="py-3 first:pt-0 last:pb-0">
                            <div
                              className={cn(
                                "flex items-center gap-2",
                                !canAccessExternalReviewLinks && "opacity-70"
                              )}
                            >
                              <Checkbox
                                checked={hideReviewLink}
                                onClick={() => setHideReviewLink((v) => !v)}
                                disabled={!canAccessExternalReviewLinks}
                              >
                                Hide the external review links window
                              </Checkbox>
                              {!canAccessExternalReviewLinks && (
                                <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-neutral-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide whitespace-nowrap text-neutral-600 ring-1 ring-neutral-200">
                                  <i
                                    className="fa-regular fa-lock"
                                    aria-hidden
                                  />
                                  {chipText}
                                </span>
                              )}
                            </div>
                            {!canAccessExternalReviewLinks && (
                              <p className="mt-0.5 ml-6 text-xs text-neutral-500">
                                {onAcceleratePlus
                                  ? "External review links need the External Approvals add-on on your current plan."
                                  : "External review links are included on Accelerate, Enterprise and Custom plans."}{" "}
                                <button
                                  type="button"
                                  onClick={() => goToManagePlan()}
                                  className="font-medium text-blue-600 underline underline-offset-2 hover:text-blue-700"
                                >
                                  Open Manage Plan
                                </button>{" "}
                                to {onAcceleratePlus ? "add it" : "upgrade"} and
                                configure this option.
                              </p>
                            )}
                          </li>
                          <li className="py-3 first:pt-0 last:pb-0">
                            <Checkbox
                              checked={canCreateExternalComments}
                              onClick={() =>
                                setCanCreateExternalComments((v) => !v)
                              }
                              disabled={!canAccessExternalReviewLinks}
                            >
                              Allow sending external notes to reviewers
                            </Checkbox>
                          </li>
                        </ul>
                      </section>
                    );
                  })()}

                <PermissionSection
                  tier={tier}
                  heading="Permissions"
                  description="What they can do beyond posting. Defaults give broad read access and keep destructive actions off."
                >
                  <ul role="list" className="divide-y divide-neutral-100">
                    <li className="py-3 first:pt-0 last:pb-0">
                      <Checkbox
                        checked={canAddAccounts}
                        onClick={() => setCanAddAccounts((v) => !v)}
                      >
                        Can add new social accounts
                      </Checkbox>
                      <p className="mt-0.5 ml-6 text-xs text-neutral-500">
                        Only administrators can remove accounts. Any user can
                        reconnect an account they already have access to.
                      </p>
                    </li>
                    <li className="py-3 first:pt-0 last:pb-0">
                      <Checkbox
                        checked={canManageUsers}
                        onClick={() => setCanManageUsers((v) => !v)}
                      >
                        Can invite, edit and remove users
                      </Checkbox>
                    </li>
                    {integrationsEnabled && (
                      <li className="py-3 first:pt-0 last:pb-0">
                        <Checkbox
                          checked={canConfigureIntegrations}
                          onClick={() => setCanConfigureIntegrations((v) => !v)}
                        >
                          Can configure integrations
                        </Checkbox>
                        <p className="mt-0.5 ml-6 text-xs text-neutral-500">
                          Lets them connect, reconnect and disconnect
                          integrations. Everyone can still view integration
                          status.
                        </p>
                      </li>
                    )}
                    <li className="py-3 first:pt-0 last:pb-0">
                      <Checkbox
                        checked={canManageContentCollects}
                        onClick={() => setCanManageContentCollects((v) => !v)}
                      >
                        Can create and manage content collection links
                      </Checkbox>
                      <p className="mt-0.5 ml-6 text-xs text-neutral-500">
                        Lets them build collection links and manage what comes
                        in. Everyone can still see the collected content in the
                        library.
                      </p>
                    </li>
                    <li className="py-3 first:pt-0 last:pb-0">
                      <Checkbox
                        checked={canAccessPaidSocial}
                        onClick={() => setCanAccessPaidSocial((v) => !v)}
                      >
                        Can view Paid Social reports
                      </Checkbox>
                    </li>
                    <li className="py-3 first:pt-0 last:pb-0">
                      <Checkbox
                        checked={canBoostPosts}
                        onClick={() => setCanBoostPosts((v) => !v)}
                      >
                        Can boost posts on Meta
                      </Checkbox>
                    </li>
                  </ul>
                </PermissionSection>

                {isShowInboxSection && tier === "advanced" ? (
                  <section className={SECTION_CARD_CLASS}>
                    <div className="flex items-baseline gap-2">
                      <h3 className="m-0 font-inter text-sm font-semibold leading-5 text-neutral-900">
                        Inbox access
                      </h3>
                      <Switch
                        size="sm"
                        checked={canShowInbox}
                        onCheckedChange={setShowInbox}
                        aria-label="Inbox access"
                      />
                    </div>
                    <p className="mt-0 mb-3 text-sm text-neutral-600">
                      Allows user to access Inbox feeds, conversations, and
                      features.
                    </p>
                    {canShowInbox ? (
                      <div className="flex flex-col gap-3">
                        <InboxPermissionSelect
                          label="Boards & feeds"
                          value={inboxBoardsAndFeedsPermission}
                          descriptions={BOARD_FEED_PERMISSION_DESCRIPTIONS}
                          onValueChange={setInboxBoardsAndFeedsPermission}
                        />
                        <InboxPermissionSelect
                          label="Reply templates"
                          value={inboxReplyTemplatesPermission}
                          descriptions={REPLY_TEMPLATE_PERMISSION_DESCRIPTIONS}
                          onValueChange={setInboxReplyTemplatesPermission}
                        />
                      </div>
                    ) : null}
                  </section>
                ) : isShowInboxSection ? (
                  <PermissionSection
                    tier={tier}
                    heading="Inbox access"
                    description="Allows user to access Inbox feeds, conversations, and features."
                  >
                    <></>
                  </PermissionSection>
                ) : null}

                <PermissionSection
                  tier={tier}
                  heading="Library access"
                  description="Restrict teammates to specific content library collections."
                >
                  <Checkbox
                    label="Restrict access to selected collections only"
                    checked={hideOtherCollections}
                    onClick={() => setHideOtherCollections((v) => !v)}
                  />
                  {hideOtherCollections && (
                    <div className="mt-2">
                      <CollectionSettings
                        collectionUserCanAccess={collectionUserCanAccess}
                        setCollectionUserCanAccess={setCollectionUserCanAccess}
                        collections={collections}
                      />
                    </div>
                  )}
                </PermissionSection>

                {postStatusList.length > 0 && (
                  <PermissionSection
                    tier={canEditPostStatusRestrictions ? "advanced" : tier}
                    heading="Post status restrictions"
                    description="By default teammates can move posts to any status. Limit which statuses they can pick below."
                    dataTestId="postStatusRestriction"
                  >
                    <AllowPostStatusList
                      postStatusList={postStatusList}
                      setPostStatusList={setPostStatusList}
                      disabled={isStatusProjectionPending}
                    />
                    {isStatusProjectionPending && (
                      <p role="status" className="text-sm text-neutral-600">
                        Your post statuses need to finish updating before these
                        permissions can be changed.
                      </p>
                    )}
                  </PermissionSection>
                )}

                <PermissionSection
                  tier={tier}
                  heading="Custom Reports access"
                  description="Reports are scoped to the accounts above."
                >
                  <RadioGroup
                    value={reportBuilderPermission}
                    onValueChange={(v) =>
                      setReportBuilderPermission(v as ReportBuilderPermission)
                    }
                    className="flex flex-col gap-2"
                  >
                    <label
                      htmlFor="rb-full-access"
                      className="m-0 flex cursor-pointer items-center gap-2 py-0.5 font-inter text-sm font-normal leading-5 text-neutral-800"
                    >
                      <RadioGroupItem value="full-access" id="rb-full-access" />
                      <span>
                        <span className="font-medium">Full access</span> —
                        create and download reports
                      </span>
                    </label>
                    <label
                      htmlFor="rb-download-only"
                      className="m-0 flex cursor-pointer items-center gap-2 py-0.5 font-inter text-sm font-normal leading-5 text-neutral-800"
                    >
                      <RadioGroupItem
                        value="download-only"
                        id="rb-download-only"
                      />
                      <span>
                        <span className="font-medium">Download only</span> —
                        download and send existing reports
                      </span>
                    </label>
                    <label
                      htmlFor="rb-no-access"
                      className="m-0 flex cursor-pointer items-center gap-2 py-0.5 font-inter text-sm font-normal leading-5 text-neutral-800"
                    >
                      <RadioGroupItem value="no-access" id="rb-no-access" />
                      <span className="font-medium">No access</span>
                    </label>
                  </RadioGroup>
                </PermissionSection>

                {slEnabled && (
                  <PermissionSection
                    tier={tier}
                    heading="Social Listening"
                    description="Choose which Social Listening features — sentiment, audience, competitors, keywords — teammates can access."
                  >
                    <div className="flex flex-col gap-1.5">
                      {slSettingsConfig.map((item) => (
                        <Checkbox
                          key={item.name}
                          disabled={!item.enabled}
                          label={item.name}
                          checked={
                            socialListeningPermission[item.settingsParam]
                          }
                          onClick={() =>
                            setSlPermission({
                              ...socialListeningPermission,
                              [item.settingsParam]:
                                !socialListeningPermission[item.settingsParam],
                            })
                          }
                        />
                      ))}
                    </div>
                  </PermissionSection>
                )}
              </div>
            </div>
          )}

          {submitError && (
            <div
              role="alert"
              className="border-t border-red-200 bg-red-50 px-6 py-3 text-sm text-red-700"
            >
              {submitError}
            </div>
          )}

          <SheetFooter className="flex flex-row items-center justify-between border-t border-neutral-100 px-6 py-4 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]">
            <div>
              {!isNewUser && (
                <Button
                  variant="red-secondary"
                  size="md"
                  type="button"
                  data-testid="deleteUser"
                  onClick={handleDelete}
                >
                  Delete user
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="md"
                type="button"
                onClick={handleClose}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Button
                      variant="blue-primary"
                      size="md"
                      type="button"
                      disabled={disabledSubmit || isLoading}
                      onClick={submit}
                    >
                      {isLoading
                        ? "Saving..."
                        : isNewUser
                          ? "Send invite"
                          : "Save changes"}
                    </Button>
                  </span>
                </TooltipTrigger>
                {disabledSubmit && (
                  <TooltipContent className="text-xs">
                    {seatCheckFailed
                      ? "Couldn't verify your seat limit. Please try again in a moment."
                      : seatsFull
                        ? "Every seat on your plan is in use, counting pending invites."
                        : isNewUser && !(email && firstName && lastName)
                          ? "Add an email, first name and last name."
                          : "Loading user data..."}
                  </TooltipContent>
                )}
              </Tooltip>
            </div>
          </SheetFooter>
        </div>
      </SheetContent>
    </Sheet>
  );
}
